import React, { useState, useEffect } from 'react';
import { API_BASE } from '../../config';
import { useTranslation } from '../../i18n/LanguageContext';
import { BarChart3, TrendingUp, Target, FileText, Award, Calendar } from 'lucide-react';
import './Performance.css';

function PerformanceStats({ user, onNavigate, hideHeader = false, t }) {
  const { t: translation } = useTranslation();
  const translationFunction = t || translation;
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  useEffect(() => {
    loadStats();
  }, [selectedYear]);

  const loadStats = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/performance/stats/detailed?period_year=${selectedYear}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!res.ok) throw new Error(translationFunction('performance.error_loading'));
      
      const data = await res.json();
      setStats(data);
    } catch (error) {
      console.error('Erreur chargement stats:', error);
      alert(translationFunction('performance.error_loading'));
    } finally {
      setLoading(false);
    }
  };

  const BarChart = ({ data, maxValue, color = '#3B82F6' }) => {
    if (!data || data.length === 0) return <div className="empty-state">{translationFunction('performance.no_data')}</div>;
    
    const max = maxValue || Math.max(...data.map(d => d.value || 0));
    
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {data.map((item, index) => (
          <div key={index}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', fontSize: '12px' }}>
              <span>{item.label}</span>
              <span style={{ fontWeight: 600 }}>{item.value?.toFixed(1) || 0}</span>
            </div>
            <div className="progress-bar" style={{ height: '24px' }}>
              <div
                className="progress-fill"
                style={{
                  width: `${((item.value || 0) / max) * 100}%`,
                  backgroundColor: color,
                  height: '100%',
                  borderRadius: '4px',
                  transition: 'width 0.5s'
                }}
              />
            </div>
          </div>
        ))}
      </div>
    );
  };

  const LineChart = ({ data, labelKey = 'month', valueKey = 'value' }) => {
    if (!data || data.length === 0) return <div className="empty-state">{translationFunction('performance.no_data')}</div>;
    
    const values = data.map(d => parseFloat(d[valueKey]) || 0);
    const max = Math.max(...values, 1);
    const min = Math.min(...values);
    const range = max - min || 1;
    const locale = navigator.language || 'fr-FR';
    
    return (
      <div style={{ position: 'relative', height: '200px', padding: '20px 0' }}>
        <svg width="100%" height="100%" style={{ overflow: 'visible' }}>
          <polyline
            points={data.map((d, i) => {
              const x = (i / (data.length - 1 || 1)) * 100;
              const y = 100 - (((parseFloat(d[valueKey]) || 0) - min) / range) * 80;
              return `${x}%,${y}%`;
            }).join(' ')}
            fill="none"
            stroke="#3B82F6"
            strokeWidth="3"
            strokeLinecap="round"
          />
          {data.map((d, i) => {
            const x = (i / (data.length - 1 || 1)) * 100;
            const y = 100 - (((parseFloat(d[valueKey]) || 0) - min) / range) * 80;
            return (
              <circle
                key={i}
                cx={`${x}%`}
                cy={`${y}%`}
                r="4"
                fill="#3B82F6"
              />
            );
          })}
        </svg>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px', fontSize: '10px', color: 'rgba(255,255,255,0.5)' }}>
          {data.map((d, i) => (
            <span key={i}>{new Date(d[labelKey]).toLocaleDateString(locale, { month: 'short' })}</span>
          ))}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className={hideHeader ? '' : 'performance-container'}>
        <div className="loading">{translationFunction('performance.loading')}</div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className={hideHeader ? '' : 'performance-container'}>
        <div className="empty-state">
          <BarChart3 size={64} color="#9CA3AF" />
          <p>{translationFunction('performance.no_stats')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={hideHeader ? '' : 'performance-container'}>
      {!hideHeader && (
      <div className="objectives-header">
        <div>
          <h1><BarChart3 size={32} /> {translationFunction('performance.stats_title')}</h1>
          <p>{translationFunction('performance.stats_subtitle')}</p>
        </div>
        <select
          value={selectedYear}
          onChange={(e) => setSelectedYear(parseInt(e.target.value))}
          style={{ padding: '10px 16px', borderRadius: '8px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff' }}
        >
          {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(year => (
            <option key={year} value={year}>{year}</option>
          ))}
        </select>
      </div>
      )}

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 320px), 1fr))',
        gap: '24px',
        marginTop: '24px',
        width: '100%',
        boxSizing: 'border-box'
      }}>
        {/* Objectifs par Statut */}
        <div className="objective-card" style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
            <Target size={24} />
            <h3>{translationFunction('performance.objectives_by_status')}</h3>
          </div>
          <BarChart
            data={stats.objectives_by_status?.map(item => ({
              label: item.status === 'active' ? translationFunction('performance.status_active') : item.status === 'completed' ? translationFunction('performance.status_completed') : item.status === 'cancelled' ? translationFunction('performance.status_cancelled') : item.status,
              value: parseFloat(item.count) || 0
            })) || []}
            color="#3B82F6"
          />
        </div>

        {/* Objectifs par Période */}
        <div className="objective-card" style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
            <Calendar size={24} />
            <h3>{translationFunction('performance.objectives_by_period')}</h3>
          </div>
          <BarChart
            data={stats.objectives_by_period?.map(item => ({
              label: item.period_type === 'annual' ? translationFunction('performance.annual') : item.period_type === 'quarterly' ? translationFunction('performance.quarterly') : translationFunction('performance.monthly'),
              value: parseFloat(item.count) || 0
            })) || []}
            color="#10B981"
          />
        </div>

        {/* Évaluations par Type */}
        <div className="objective-card" style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
            <FileText size={24} />
            <h3>{translationFunction('performance.evaluations_by_type')}</h3>
          </div>
          <BarChart
            data={stats.evaluations_by_type?.map(item => ({
              label: item.evaluation_type === 'annual' ? translationFunction('performance.evaluation_type_annual') : item.evaluation_type === 'probationary' ? translationFunction('performance.evaluation_type_probationary') : item.evaluation_type === 'mid_year' ? translationFunction('performance.evaluation_type_mid_year') : item.evaluation_type === 'project' ? translationFunction('performance.evaluation_type_project') : translationFunction('performance.evaluation_type_custom'),
              value: parseFloat(item.count) || 0
            })) || []}
            color="#8B5CF6"
          />
        </div>

        {/* Évolution Mensuelle */}
        <div className="objective-card" style={{ gridColumn: '1 / -1', minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
            <TrendingUp size={24} />
            <h3>{translationFunction('performance.monthly_progress_evolution')}</h3>
          </div>
          <LineChart
            data={stats.monthly_progress?.map(item => ({
              month: item.month,
              value: parseFloat(item.avg_progress) || 0
            })) || []}
            valueKey="value"
          />
        </div>

        {/* Résumé */}
        <div className="objective-card" style={{ gridColumn: '1 / -1', minWidth: 0 }}>
          <h3 style={{ marginBottom: '20px' }}>{translationFunction('performance.summary')}</h3>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 180px), 1fr))',
            gap: '16px'
          }}>
            <div style={{ padding: '16px', background: 'rgba(255,255,255,0.05)', borderRadius: '12px' }}>
              <div style={{ fontSize: '32px', fontWeight: 700, color: '#3B82F6' }}>
                {stats.objectives_by_status?.reduce((sum, item) => sum + (parseInt(item.count) || 0), 0) || 0}
              </div>
              <div style={{ fontSize: '14px', color: 'rgba(255,255,255,0.7)', marginTop: '4px' }}>
                {translationFunction('performance.total_objectives')}
              </div>
            </div>
            <div style={{ padding: '16px', background: 'rgba(255,255,255,0.05)', borderRadius: '12px' }}>
              <div style={{ fontSize: '32px', fontWeight: 700, color: '#10B981' }}>
                {stats.objectives_by_status?.find(item => item.status === 'completed')?.count || 0}
              </div>
              <div style={{ fontSize: '14px', color: 'rgba(255,255,255,0.7)', marginTop: '4px' }}>
                {translationFunction('performance.objectives_completed')}
              </div>
            </div>
            <div style={{ padding: '16px', background: 'rgba(255,255,255,0.05)', borderRadius: '12px' }}>
              <div style={{ fontSize: '32px', fontWeight: 700, color: '#8B5CF6' }}>
                {stats.evaluations_by_type?.reduce((sum, item) => sum + (parseInt(item.count) || 0), 0) || 0}
              </div>
              <div style={{ fontSize: '14px', color: 'rgba(255,255,255,0.7)', marginTop: '4px' }}>
                {translationFunction('performance.total_evaluations')}
              </div>
            </div>
            <div style={{ padding: '16px', background: 'rgba(255,255,255,0.05)', borderRadius: '12px' }}>
              <div style={{ fontSize: '32px', fontWeight: 700, color: '#F59E0B' }}>
                {(() => {
                  if (!stats.evaluations_by_type || stats.evaluations_by_type.length === 0) return '0.0';
                  const totalScore = stats.evaluations_by_type.reduce((sum, item) => {
                    return sum + (parseFloat(item.avg_score) || 0) * (parseInt(item.count) || 0);
                  }, 0);
                  const totalCount = stats.evaluations_by_type.reduce((sum, item) => {
                    return sum + (parseInt(item.count) || 0);
                  }, 0);
                  return totalCount > 0 ? (totalScore / totalCount).toFixed(1) : '0.0';
                })()}
              </div>
              <div style={{ fontSize: '14px', color: 'rgba(255,255,255,0.7)', marginTop: '4px' }}>
                {translationFunction('performance.average_score')}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PerformanceStats;

